import { Row } from 'react-bootstrap';
import { Input } from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { useContext, useEffect, useMemo, useState } from 'react';

import { TaskCard } from './task-card';
import { TaskEntity } from '../../../types';
import { PlayerContextState } from '../../../contexts/player';
import { ManualScoring } from '../../../types/manual-scoring';
import { GridMode, GridToolBar } from '../../grid/grid-tool-bar';
import { Task } from '../../../types/game-document/entities/task';
import { GetTaskById, GetTaskTitle } from '../../../utils/game-document/assets';
import { GameDocumentContext } from '../../../contexts/game-document';
import { GetResourceValue } from '../../../utils/game-document/resources';
import { TaskContentForm } from '../../../types/game-document/entities/task-content';
import { GetQuestionsFromTaskContent } from '../../../utils/game-document/assets/task-contents';
import cloneDeep from 'lodash.clonedeep';
import { downloadFilesAndZip } from '../../../utils/file-utility';
import { FileZipDownloadRequest } from '../../../types/facilitator/file-zip-download';
import { useDownloadSelected } from '../../../hooks/use-download-selected';

interface TaskGridProps {
  playerStates?: PlayerContextState[];
  onViewChange?: (view: GridMode) => void;
  viewMode?: GridMode;
  onSubmitScore?: (e: ManualScoring) => void;
  onExport?: () => void;
}

export const TaskGrid = ({ playerStates, ...props }: TaskGridProps) => {
  const [gameDocument] = useContext(GameDocumentContext);
  const [selectedTask, setSelectedTask] = useState<Task>();
  const [selectedForm, setSelectedForm] = useState<{
    id: string;
    description: string;
  }>();
  const [searchText, setSearchText] = useState<string>('');

  const tasks = gameDocument.gameDocument?.assets.tasks ?? [];
  const {
    selectedDownload,
    onResetSelected,
    onDownloadSelected,
    onAddRemoveSelectedDownload
  } = useDownloadSelected(`${gameDocument?.gameCode!}-download-selected.zip`);

  const formQuestion = useMemo(() => {
    if (!selectedTask?.taskContentId) return [];

    const question =
      GetQuestionsFromTaskContent(
        gameDocument?.gameDocument!,
        selectedTask?.taskContentId
      ) ?? [];

    setSelectedForm(question[0]);

    return question;
  }, [selectedTask?.taskContentId]);

  useEffect(() => {
    onResetSelected();
  }, [selectedTask, selectedForm]);

  useEffect(() => {
    if (!selectedTask) {
      if (tasks.length > 0) {
        setSelectedTask(tasks[0]);
      }
    }
  }, [tasks]);

  return (
    <>
      <div className={'d-flex mb-3 w-full'}>
        <GridToolBar
          className={'grid-toolbar w-full justify-content-end'}
          showSearch={false}
          showClear={false}
          searchPlaceholder={'Search'}
          columnsToSearch={['taskName', 'question', 'questionType', 'score']}
          onGridModeChange={(e) => {
            if (props.onViewChange) {
              props.onViewChange(e.gridMode);
            }
          }}
          viewMode={props.viewMode}
          handleExport={() => {
            if (props.onExport) {
              props.onExport();
            }
          }}
        />
      </div>
      <div className={'d-flex h-100 flex-grow-1 overflow-hidden'}>
        <div className={'d-flex flex-column group-menu'}>
          <div className={'border-list'}>
            <div className={'p-1 sticky-top bg-white'}>
              <Input
                id={'search-text'}
                type={'text'}
                className={'form-control'}
                placeholder={'Search task..'}
                onChange={(e) =>
                  setSearchText((e.target.value as string) ?? '')
                }
              />
            </div>
            <ul className="list-group facilitator-border-none">
              {tasks
                .filter((x) => {
                  if (
                    x.taskContentId === undefined ||
                    x.taskContentId === null ||
                    x.taskContentId === ''
                  ) {
                    return false;
                  }
                  const taskTitle = GetTaskTitle(
                    gameDocument?.gameDocument,
                    x.id
                  );
                  const searchLower = searchText?.toLowerCase() ?? '';
                  return (
                    taskTitle?.toLowerCase()?.includes(searchLower) ||
                    x.name?.toLowerCase()?.includes(searchLower)
                  );
                })
                .map((item, index) => {
                  return (
                    <li
                      key={index}
                      className="list-group-item cursor-pointer"
                      onClick={() => {
                        setSelectedTask(item);
                        setSelectedForm(undefined);
                      }}>
                      <div className={'d-flex justify-content-between'}>
                        <small
                          className={`${
                            item.id === selectedTask?.id
                              ? 'fw-bold text-dark'
                              : 'text-dark'
                          }`}>
                          {GetTaskTitle(gameDocument?.gameDocument, item!.id!)}
                        </small>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>

        <div className={'group-content'}>
          <div className={'ml-5'}>
            <div className={'d-flex justify-content-between'}>
              <span className={'fw-bold'}>
                {GetResourceValue(
                  gameDocument?.gameDocument!,
                  selectedTask?.titleResId!,
                  ''
                )}
              </span>
              {selectedDownload.length > 0 && (
                <span
                  className={'text-primary cursor-pointer'}
                  onClick={onDownloadSelected}>
                  Download selected
                </span>
              )}
            </div>

            {formQuestion.length > 1 && (
              <div className={'d-flex mt-3 flex-column'}>
                <span className={'text-dark'}>Select a question</span>
                <DropDownList
                  data={formQuestion}
                  value={
                    formQuestion?.find((x) => x.id === selectedForm?.id) ?? null
                  }
                  textField={'description'}
                  onChange={(e) => setSelectedForm(e.value)}
                />
              </div>
            )}

            <div className={'mt-4'}>
              <Row>
                {playerStates?.map(
                  (playerState: PlayerContextState, outerIndex: number) => {
                    return playerState?.playerState?.tasks
                      ?.filter((task) => task.id === selectedTask?.id)
                      .map((task: TaskEntity) => {
                        return task?.taskContentFormAnswers
                          ?.filter((x) => x.formId === selectedForm?.id)
                          ?.map((taskContentAnswer, taskContentAnswerIndex) => {
                            return (
                              <TaskCard
                                key={taskContentAnswer.formId}
                                taskContentAnswer={taskContentAnswer}
                                index={taskContentAnswerIndex}
                                selectedTask={selectedTask!}
                                playerName={playerState?.playerState?.name}
                                playerCode={playerState?.playerState?.code}
                                onSubmitScore={(e) => {
                                  if (props.onSubmitScore) {
                                    props.onSubmitScore(e);
                                  }
                                }}
                                selectedDownload={selectedDownload}
                                onAddRemoveSelectedDownload={
                                  onAddRemoveSelectedDownload
                                }
                              />
                            );
                          });
                      });
                  }
                )}
              </Row>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
